import { ReactNode } from 'react'
import { SocialCitationPost } from '@digital-twins/shared-types'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import Link from '@/routing/Link'
import { useTranslation } from 'react-i18next'
import { SocialPostEmbedCard } from './SocialPostEmbedCard'

interface SocialCitationDigestProps {
  topicName: string
  citationLabel: string
  /** The Topic page; null hides "Open in social listening". */
  topicHref: string | null
  /** The posts the cited passage overlaps. Not a claim that each was used. */
  posts?: Array<SocialCitationPost>
  /** "{n} posts, {window}" when the citation names no specific post. */
  countLine?: string | null
  /** Shown under the cards, for example the loader while the posts resolve. */
  children?: ReactNode
}

/**
 * A social digest citation that is not one post: the Topic, the link to its
 * page, and either the posts the passage overlaps or how many posts the digest
 * holds. Never the digest's raw text.
 */
export function SocialCitationDigest(props: SocialCitationDigestProps) {
  const { topicName, citationLabel, topicHref, posts = [], countLine = null, children } = props
  const { t } = useTranslation('app')

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="thread-source-view">
      <div className="space-y-3 border-b border-border px-5 py-4 text-left">
        <div className="min-w-0 space-y-1">
          <h2
            className="line-clamp-3 font-serif text-xl font-normal leading-snug"
            data-testid="thread-source-title"
          >
            {topicName}
          </h2>
          <Text size="xs" color="muted">
            {citationLabel}
          </Text>
        </div>
        {topicHref ? (
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm" className="h-8">
              <Link href={topicHref} data-testid="thread-source-open-social-listening">
                {t('socialListening.citation.openInSocialListening')}
              </Link>
            </Button>
          </div>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {countLine ? (
          <div data-testid="thread-source-social-count">
            <Text as="p" size="sm" color="muted">
              {countLine}
            </Text>
          </div>
        ) : null}
        {posts.length > 0 ? (
          <div className="@container space-y-3" data-testid="thread-source-social-posts">
            {posts.map((post, index) => (
              <SocialPostEmbedCard
                key={post.postToken}
                // Reddit has no player here: a card shows its text or the platform icon.
                post={post.platform === 'reddit' ? { ...post, imagePath: null } : post}
                testId={`thread-source-social-post-${index}`}
              />
            ))}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  )
}
